import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { StatusBadge } from "../components/etams/StatusBadge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";

const KOLOM_CSV = [
  { kunci: "package_type", judul: "Paket", ambil: (t) => PKG_LABEL[t.package_type] || t.package_type },
  { kunci: "price_group_id", judul: "Price Group ID" },
  { kunci: "surcharge_amount_idr", judul: "Biaya Tambahan (IDR)" },
  { kunci: "surcharge_percentage", judul: "Biaya Tambahan (%)" },
  { kunci: "effective_from", judul: "Berlaku Dari" },
  { kunci: "effective_to", judul: "Berlaku Sampai" },
  { kunci: "active", judul: "Aktif", ambil: (t) => (t.active ? "Ya" : "Tidak") },
];

const PKG_TYPES = ["REGULER", "EXPRESS", "SUPER_EXPRESS"];
const PKG_LABEL = { REGULER: "Reguler", EXPRESS: "Express", SUPER_EXPRESS: "Super Express" };

export default function MasterTimePackage() {
  const { isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPg, setFilterPg] = useState("ALL");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterPg !== "ALL") params.price_group_id = filterPg;
      const [tp, pg] = await Promise.all([
        api.get("/master/time-package", { params }),
        api.get("/master/price-groups"),
      ]);
      setItems(tp.data.items || []);
      setGroups(pg.data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Paket waktu gagal dimuat")); }
    finally { setLoading(false); }
  }, [filterPg]);
  useEffect(() => { load(); }, [load]);
  const daftar = useDaftar(items, {
    kunciCari: ["package_type"],
    urutAwal: { kunci: "package_type", arah: "asc" },
  });

  const pgName = (id) => groups.find((g) => g.id === id)?.code || (id ? "—" : "Global");

  const handleDelete = async () => {
    try { await api.delete(`/master/time-package/${confirmDel.id}`); toast.success("Aturan dihapus"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Aturan gagal dihapus")); }
    finally { setConfirmDel(null); }
  };

  return (
    <div className="space-y-6" data-testid="master-tp-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Paket Waktu Pengerjaan{" "}
            <Hint>Biaya tambahan per paket waktu (Reguler 14, Express 7, Super Express 3 hari kerja). Diisi per pcs (IDR) atau persentase.</Hint>
          </h1>
        </div>
        {isOwner && <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="tp-new-btn"><Plus size={16} className="mr-1" /> Tambah</Button>}
      </div>
      <Card className="p-4">
        <div className="mb-4">
          <Select value={filterPg} onValueChange={setFilterPg}>
            <SelectTrigger className="w-64" data-testid="tp-filter-pg"><SelectValue placeholder="Price Group" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Price Group</SelectItem>
              {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.code} — {g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <PalangDaftar daftar={daftar} placeholder="Cari paket…" testId="tp-palang"
                      ekspor={{ nama: "master_paket_waktu", kolom: KOLOM_CSV }} />
        {loading ? <div className="py-8 text-center text-sm text-muted-foreground">Memuat...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <ThUrut daftar={daftar} kunci="package_type" className="text-left px-3 py-2">Paket</ThUrut>
                  <th className="text-left px-3 py-2">Price Group</th>
                  <ThUrut daftar={daftar} kunci="surcharge_amount_idr" className="text-right px-3 py-2" align="right">Biaya Tambahan (IDR)</ThUrut>
                  <ThUrut daftar={daftar} kunci="surcharge_percentage" className="text-right px-3 py-2" align="right">Biaya Tambahan (%)</ThUrut>
                  <th className="text-left px-3 py-2">Berlaku</th>
                  <ThUrut daftar={daftar} kunci="active" className="text-left px-3 py-2">Status</ThUrut>
                  {isOwner && <th></th>}
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && <tr><td colSpan={isOwner ? 7 : 6} className="px-3 py-8 text-center text-muted-foreground">
                  {daftar.cari.trim() ? "Tidak ada yang cocok." : "Belum ada aturan."}
                </td></tr>}
                {daftar.tampil.map((t) => (
                  <tr key={t.id} className="border-t border-border" data-testid={`tp-row-${t.id}`}>
                    <td className="px-3 py-2"><Badge variant="outline">{PKG_LABEL[t.package_type] || t.package_type}</Badge></td>
                    <td className="px-3 py-2 text-xs">{pgName(t.price_group_id)}</td>
                    <td className="px-3 py-2 text-right font-mono">{t.surcharge_amount_idr != null ? formatIDR(t.surcharge_amount_idr) : "—"}</td>
                    <td className="px-3 py-2 text-right">{t.surcharge_percentage != null ? `${t.surcharge_percentage}%` : "—"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{t.effective_from || "—"} → {t.effective_to || "∞"}</td>
                    <td className="px-3 py-2">{t.active ? <StatusBadge tone="success">Aktif</StatusBadge> : <Badge variant="outline">Nonaktif</Badge>}</td>
                    {isOwner && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => { setEditing(t); setShowForm(true); }} data-testid={`tp-edit-${t.id}`}><Edit size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDel(t)} className="text-danger-soft-foreground" data-testid={`tp-delete-${t.id}`}><Trash2 size={14} /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="tp-paginasi" />
          </div>
        )}
      </Card>
      {showForm && <TpFormModal tp={editing} groups={groups} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(); }} />}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus Paket Waktu?</AlertDialogTitle><AlertDialogDescription>"{PKG_LABEL[confirmDel?.package_type] || confirmDel?.package_type}" akan dihapus.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="tp-delete-confirm">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TpFormModal({ tp, groups, onClose, onSaved }) {
  const [f, setF] = useState({
    package_type: tp?.package_type || PKG_TYPES[0],
    surcharge_amount_idr: tp?.surcharge_amount_idr ?? "",
    surcharge_percentage: tp?.surcharge_percentage ?? "",
    price_group_id: tp?.price_group_id || "",
    effective_from: tp?.effective_from || "",
    effective_to: tp?.effective_to || "",
    active: tp?.active ?? true,
    notes: tp?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (f.surcharge_amount_idr === "" && f.surcharge_percentage === "") { toast.error("Isi biaya tambahan IDR atau persentase"); return; }
    setSaving(true);
    try {
      const body = {
        package_type: f.package_type,
        surcharge_amount_idr: f.surcharge_amount_idr === "" ? null : parseInt(f.surcharge_amount_idr),
        surcharge_percentage: f.surcharge_percentage === "" ? null : parseFloat(f.surcharge_percentage),
        price_group_id: f.price_group_id || null,
        effective_from: f.effective_from || null,
        effective_to: f.effective_to || null,
        active: f.active,
        notes: f.notes || null,
      };
      if (tp) await api.patch(`/master/time-package/${tp.id}`, body);
      else await api.post("/master/time-package", body);
      toast.success("Disimpan"); onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Paket waktu gagal disimpan")); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="tp-form-modal">
        <DialogHeader><DialogTitle>{tp ? "Ubah Paket Waktu" : "Tambah Paket Waktu"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            <Label>Jenis Paket *</Label>
            <Select value={f.package_type} onValueChange={(v) => upd("package_type", v)}>
              <SelectTrigger data-testid="tp-form-pkg"><SelectValue /></SelectTrigger>
              <SelectContent>{PKG_TYPES.map((p) => <SelectItem key={p} value={p}>{PKG_LABEL[p]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Price Group (opsional — kosong = global)</Label>
            <Select value={f.price_group_id || "NULL"} onValueChange={(v) => upd("price_group_id", v === "NULL" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Global / semua price group" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NULL">Global (semua price group)</SelectItem>
                {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.code} — {g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Biaya Tambahan IDR / pcs</Label><Input type="number" min={0} value={f.surcharge_amount_idr} onChange={(e) => upd("surcharge_amount_idr", e.target.value)} data-testid="tp-form-amount" /></div>
          <div><Label>Biaya Tambahan %</Label><Input type="number" step="0.01" value={f.surcharge_percentage} onChange={(e) => upd("surcharge_percentage", e.target.value)} /></div>
          <div><Label>Berlaku Dari</Label><Input type="date" value={f.effective_from} onChange={(e) => upd("effective_from", e.target.value)} /></div>
          <div><Label>Berlaku Sampai</Label><Input type="date" value={f.effective_to} onChange={(e) => upd("effective_to", e.target.value)} /></div>
          <div className="col-span-2 flex items-center gap-2"><Checkbox checked={f.active} onCheckedChange={(v) => upd("active", !!v)} id="tpactive" /><Label htmlFor="tpactive" className="cursor-pointer">Aktif</Label></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="tp-form-save">{saving ? "Menyimpan..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
